// Browser-side image preprocessing for OCR:
//  1. EXIF orientation normalization
//  2. Auto-crop near-white borders (document edge detection)
//  3. Light deskew (projection-profile rotation search, small angles)
//  4. Downscale + JPEG compress for upload
// (browser-image-compression is dynamically imported inside preprocessImageFile
//  to keep this module SSR-safe.)

const MAX_DIM = 2000; // upper bound for OCR-friendly size

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function canvasFromImage(img: HTMLImageElement | HTMLCanvasElement): HTMLCanvasElement {
  const w = (img as HTMLImageElement).naturalWidth ?? (img as HTMLCanvasElement).width;
  const h = (img as HTMLImageElement).naturalHeight ?? (img as HTMLCanvasElement).height;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d")!;
  ctx.drawImage(img as CanvasImageSource, 0, 0);
  return c;
}

// Auto-crop near-white margins. Threshold luma > 240 considered background.
function autoCrop(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = canvas.getContext("2d")!;
  const { width: w, height: h } = canvas;
  const data = ctx.getImageData(0, 0, w, h).data;
  const isContent = (x: number, y: number) => {
    const i = (y * w + x) * 4;
    const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    return luma < 220;
  };
  let top = 0,
    bottom = h - 1,
    left = 0,
    right = w - 1;
  const step = Math.max(1, Math.floor(Math.min(w, h) / 600));
  outerTop: for (; top < h; top += step)
    for (let x = 0; x < w; x += step) if (isContent(x, top)) break outerTop;
  outerBot: for (; bottom > top; bottom -= step)
    for (let x = 0; x < w; x += step) if (isContent(x, bottom)) break outerBot;
  outerLeft: for (; left < w; left += step)
    for (let y = top; y <= bottom; y += step) if (isContent(left, y)) break outerLeft;
  outerRight: for (; right > left; right -= step)
    for (let y = top; y <= bottom; y += step) if (isContent(right, y)) break outerRight;
  // Add small padding
  const pad = Math.floor(Math.min(w, h) * 0.01);
  top = Math.max(0, top - pad);
  left = Math.max(0, left - pad);
  bottom = Math.min(h - 1, bottom + pad);
  right = Math.min(w - 1, right + pad);
  const cw = right - left + 1,
    ch = bottom - top + 1;
  if (cw < w * 0.5 || ch < h * 0.5) return canvas; // suspicious crop, skip
  if (cw === w && ch === h) return canvas;
  const out = document.createElement("canvas");
  out.width = cw;
  out.height = ch;
  out.getContext("2d")!.drawImage(canvas, left, top, cw, ch, 0, 0, cw, ch);
  return out;
}

// Compute horizontal projection variance for a downscaled grayscale at given rotation.
function rotationScore(gray: Uint8ClampedArray, w: number, h: number, angleRad: number): number {
  const cos = Math.cos(angleRad),
    sin = Math.sin(angleRad);
  const cx = w / 2,
    cy = h / 2;
  const rows = new Float64Array(h);
  // Sample every 2 pixels for speed
  for (let y = 0; y < h; y += 2) {
    let sum = 0;
    for (let x = 0; x < w; x += 2) {
      const sx = (x - cx) * cos + (y - cy) * sin + cx;
      const sy = -(x - cx) * sin + (y - cy) * cos + cy;
      const ix = sx | 0,
        iy = sy | 0;
      if (ix < 0 || ix >= w || iy < 0 || iy >= h) continue;
      sum += 255 - gray[iy * w + ix]; // ink intensity
    }
    rows[y] = sum;
  }
  // Variance of row sums = high when text lines align horizontally
  let mean = 0;
  let count = 0;
  for (let y = 0; y < h; y += 2) {
    mean += rows[y];
    count++;
  }
  mean /= count;
  let v = 0;
  for (let y = 0; y < h; y += 2) v += (rows[y] - mean) ** 2;
  return v;
}

// Search small angles to find best rotation.
function detectSkewAngle(canvas: HTMLCanvasElement): number {
  // Downscale to ~400px wide for speed
  const scale = Math.min(1, 400 / canvas.width);
  const w = Math.max(1, Math.round(canvas.width * scale));
  const h = Math.max(1, Math.round(canvas.height * scale));
  const tmp = document.createElement("canvas");
  tmp.width = w;
  tmp.height = h;
  tmp.getContext("2d")!.drawImage(canvas, 0, 0, w, h);
  const data = tmp.getContext("2d")!.getImageData(0, 0, w, h).data;
  const gray = new Uint8ClampedArray(w * h);
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    gray[j] = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) | 0;
  }
  let bestAngle = 0,
    bestScore = -Infinity;
  for (let deg = -8; deg <= 8; deg += 1) {
    const score = rotationScore(gray, w, h, (deg * Math.PI) / 180);
    if (score > bestScore) {
      bestScore = score;
      bestAngle = deg;
    }
  }
  // Refine ±1°
  for (let deg = bestAngle - 1; deg <= bestAngle + 1; deg += 0.25) {
    const score = rotationScore(gray, w, h, (deg * Math.PI) / 180);
    if (score > bestScore) {
      bestScore = score;
      bestAngle = deg;
    }
  }
  return bestAngle;
}

function rotateCanvas(canvas: HTMLCanvasElement, deg: number): HTMLCanvasElement {
  if (Math.abs(deg) < 0.3) return canvas;
  const rad = (deg * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad)),
    cos = Math.abs(Math.cos(rad));
  const w = canvas.width,
    h = canvas.height;
  const nw = Math.ceil(w * cos + h * sin);
  const nh = Math.ceil(w * sin + h * cos);
  const out = document.createElement("canvas");
  out.width = nw;
  out.height = nh;
  const ctx = out.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, nw, nh);
  ctx.translate(nw / 2, nh / 2);
  ctx.rotate(rad);
  ctx.drawImage(canvas, -w / 2, -h / 2);
  return out;
}

export type PreprocessReport = {
  rotated: number;
  cropped: boolean;
  finalWidth: number;
  finalHeight: number;
};

export type PreprocessStage =
  | "normalize" // EXIF orientation + downscale
  | "scan" // mobile document scanner (perspective correction)
  | "deskew" // straighten skewed text
  | "crop" // crop near-white margins
  | "finalize"; // encode JPEG

export type PreprocessStageInfo = {
  stage: PreprocessStage;
  label: string; // human-friendly sublabel for the UI
  progress: number; // 0-100, scoped to the preprocessing portion
};

const STAGE_LABELS: Record<PreprocessStage, string> = {
  normalize: "Normalizing image orientation…",
  scan: "Detecting document edges…",
  deskew: "Straightening skewed text…",
  crop: "Cropping document margins…",
  finalize: "Finalizing scan…",
};

type PreprocessOpts = { onStage?: (info: PreprocessStageInfo) => void };

export async function preprocessImageFile(
  file: File,
  opts: PreprocessOpts = {},
): Promise<{ dataUrl: string; report: PreprocessReport }> {
  const emit = (stage: PreprocessStage, progress: number) =>
    opts.onStage?.({ stage, label: STAGE_LABELS[stage], progress });

  // Step 1: EXIF normalize + downscale via browser-image-compression (dynamic import keeps SSR safe)
  emit("normalize", 5);
  const { default: imageCompression } = await import("browser-image-compression");
  const normalized = await imageCompression(file, {
    maxWidthOrHeight: MAX_DIM,
    maxSizeMB: 4,
    useWebWorker: true,
    initialQuality: 0.9,
    fileType: "image/jpeg",
  });
  emit("normalize", 25);
  const url = URL.createObjectURL(normalized);
  try {
    const img = await loadImage(url);
    let canvas = canvasFromImage(img);

    // Step 2: mobile-only document scan (perspective correction + contrast
    // enhancement via jscanify/OpenCV). Skips silently on desktop or when
    // OpenCV / edge detection fails.
    let scannerCropped = false;
    if (typeof window !== "undefined") {
      try {
        if (!import.meta.env.SSR) {
          const { shouldUseDocumentScanner, scanDocumentFromCanvas } =
            await import("./document-scanner");
          if (shouldUseDocumentScanner()) {
            emit("scan", 40);
            const scan = await scanDocumentFromCanvas(canvas);
            if (scan.cropped) {
              canvas = scan.canvas;
              scannerCropped = true;
            }
          }
        }
      } catch {
        // ignore — fall through to legacy pipeline
      }
    }

    // Step 3: deskew (skip if scanner already perspective-corrected the page)
    let angle = 0;
    if (!scannerCropped) {
      emit("deskew", 60);
      angle = detectSkewAngle(canvas);
      canvas = rotateCanvas(canvas, -angle);
    }

    // Step 4: auto-crop near-white borders (skip if scanner cropped already)
    const before = { w: canvas.width, h: canvas.height };
    if (!scannerCropped) {
      emit("crop", 80);
      canvas = autoCrop(canvas);
    }
    const cropped = scannerCropped || canvas.width !== before.w || canvas.height !== before.h;
    emit("finalize", 95);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    emit("finalize", 100);
    return {
      dataUrl,
      report: {
        rotated: Math.round(angle * 10) / 10,
        cropped,
        finalWidth: canvas.width,
        finalHeight: canvas.height,
      },
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Render a PDF page to a JPEG dataURL via pdfjs (used for scanned-page OCR fallback).
export async function renderPdfPageToDataUrl(
  pdfDoc: any,
  pageNum: number,
  targetWidth = 1600,
): Promise<string> {
  const page = await pdfDoc.getPage(pageNum);
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = Math.min(2.5, targetWidth / baseViewport.width);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport, canvas }).promise;
  return canvas.toDataURL("image/jpeg", 0.85);
}
